import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IconPlay, IconPause, IconReplay, IconBack10, IconFwd10, IconVolume, IconVolumeLow, IconMute,
  IconSettings, IconFullscreen, IconFullscreenExit, IconTheater, IconNext, IconKeyboard,
  IconChevronRight, IconChevronLeft, IconCheck, IconSpeed, IconHd,
} from './Icons';
import { storage, formatTime, progressKey, doneKey } from './utils';
import './Player.css';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const PREFS_KEY = 'vp:prefs';
const HIDE_DELAY = 2600;
const AUTONEXT_SECONDS = 8;

const SHORTCUTS = [
  ['Пробел / K', 'Пауза / воспроизведение'],
  ['← / →', 'Назад / вперёд на 5 сек'],
  ['J / L', 'Назад / вперёд на 10 сек'],
  ['↑ / ↓', 'Громкость'],
  ['M', 'Выключить звук'],
  ['F', 'Полный экран'],
  ['T', 'Широкий режим'],
  ['Shift + < / >', 'Скорость'],
  ['0 … 9', 'Перейти к 0–90%'],
  ['Shift + N', 'Следующее видео'],
  ['?', 'Горячие клавиши'],
];

const isTypingTarget = (el) =>
  el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);

function Player({
  videoRef,
  videoId,
  title,
  src,
  fallbackSrc,
  startAt = 0,
  resumedFrom = 0,
  next = null,
  onNext,
  theater = false,
  onToggleTheater,
  onFatalError,
  overlay,
  showTitle = false,
}) {
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const progressRef = useRef(null);
  const hideTimerRef = useRef(null);
  const osdTimerRef = useRef(null);
  const tapRef = useRef({ time: 0, x: 0, timer: null });
  const pointerTypeRef = useRef('mouse');
  const lastSaveRef = useRef(0);
  const menuRef = useRef(null);

  const prefs = useMemo(() => storage.get(PREFS_KEY, {}) || {}, []);

  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [waiting, setWaiting] = useState(true);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(typeof prefs.volume === 'number' ? prefs.volume : 1);
  const [muted, setMuted] = useState(!!prefs.muted);
  const [rate, setRate] = useState(SPEEDS.includes(prefs.rate) ? prefs.rate : 1);
  const [autoNext, setAutoNext] = useState(prefs.autoNext !== false);
  const [levels, setLevels] = useState([]);
  const [level, setLevel] = useState(-1);
  const [autoHeight, setAutoHeight] = useState(null);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [menu, setMenu] = useState(null); // null | 'main' | 'speed' | 'quality'
  const [hoverRatio, setHoverRatio] = useState(null);
  const [scrubRatio, setScrubRatio] = useState(null);
  const [osd, setOsd] = useState(null);
  const [ripple, setRipple] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [pseudoFullscreen, setPseudoFullscreen] = useState(false);
  const [showRemaining, setShowRemaining] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [resumeChip, setResumeChip] = useState(resumedFrom > 0 ? resumedFrom : null);

  const isFs = fullscreen || pseudoFullscreen;

  // ---------- Предпочтения зрителя ----------
  useEffect(() => {
    storage.set(PREFS_KEY, { volume, muted, rate, autoNext });
  }, [volume, muted, rate, autoNext]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.volume = volume;
    el.muted = muted;
  }, [volume, muted, videoRef]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // defaultPlaybackRate сохраняет скорость при смене источника (браузер сбрасывает playbackRate при загрузке)
    el.defaultPlaybackRate = rate;
    el.playbackRate = rate;
  }, [rate, videoRef]);

  // ---------- Загрузка HLS ----------
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !src) return undefined;
    const Hls = window.Hls;
    let hls = null;
    const autoplay = () => el.play().catch(() => setWaiting(false));

    const load = (url, isFallback) => {
      if (Hls && Hls.isSupported()) {
        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          startLevel: -1,
          capLevelToPlayerSize: true,
          startPosition: startAt > 0 ? startAt : -1,
        });
        hlsRef.current = hls;
        hls.loadSource(url);
        hls.attachMedia(el);
        hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
          setLevels((data.levels || []).map((l, index) => ({ index, height: l.height, bitrate: l.bitrate })));
          el.playbackRate = rate;
          autoplay();
        });
        hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
          const l = hls.levels && hls.levels[data.level];
          setAutoHeight(l ? l.height : null);
        });
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            if (!isFallback && fallbackSrc) {
              hls.destroy();
              load(fallbackSrc, true);
            } else {
              hls.startLoad();
            }
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else {
            hls.destroy();
            onFatalError && onFatalError('Ошибка воспроизведения. Попробуйте обновить страницу.');
          }
        });
      } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
        el.src = url;
        el.addEventListener(
          'loadedmetadata',
          () => {
            if (startAt > 0) el.currentTime = startAt;
            el.playbackRate = rate;
            autoplay();
          },
          { once: true }
        );
      } else {
        onFatalError && onFatalError('Ваш браузер не поддерживает воспроизведение этого видео.');
      }
    };

    load(src, false);
    return () => {
      if (hls) hls.destroy();
      hlsRef.current = null;
    };
    // startAt/rate читаются только при первой загрузке источника
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, fallbackSrc]);

  // ---------- События <video> ----------
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;

    const updateBuffered = () => {
      if (!el.duration || !el.buffered || !el.buffered.length) return;
      let end = 0;
      for (let i = 0; i < el.buffered.length; i += 1) {
        if (el.buffered.start(i) <= el.currentTime + 0.5) end = el.buffered.end(i);
      }
      setBuffered(Math.min(100, (end / el.duration) * 100));
    };

    const saveProgress = (force) => {
      const now = Date.now();
      if (!force && now - lastSaveRef.current < 4000) return;
      lastSaveRef.current = now;
      const t = el.currentTime;
      const d = el.duration;
      if (!d || !isFinite(d)) return;
      if (t > 5 && t < d - 10) storage.set(progressKey(videoId), Math.floor(t));
      if (t / d > 0.92) storage.set(doneKey(videoId), 1);
    };

    const handlers = {
      play: () => {
        setPlaying(true);
        setStarted(true);
        setEnded(false);
        setCountdown(null);
      },
      pause: () => {
        setPlaying(false);
        saveProgress(true);
      },
      playing: () => setWaiting(false),
      waiting: () => setWaiting(true),
      canplay: () => setWaiting(false),
      seeked: () => updateBuffered(),
      progress: updateBuffered,
      timeupdate: () => {
        setTime(el.currentTime);
        updateBuffered();
        saveProgress(false);
      },
      durationchange: () => setDuration(el.duration || 0),
      loadedmetadata: () => setDuration(el.duration || 0),
      volumechange: () => {
        setVolume(el.volume);
        setMuted(el.muted);
      },
      ratechange: () => {
        // Игнорируем сброс скорости, который браузер делает сам при загрузке нового источника
        if (el.readyState > 0) setRate(el.playbackRate);
      },
      ended: () => {
        setPlaying(false);
        setEnded(true);
        storage.set(doneKey(videoId), 1);
        storage.remove(progressKey(videoId));
      },
      // Picture-in-Picture запрещён: выходим, если браузер всё же его включил
      enterpictureinpicture: () => {
        if (document.exitPictureInPicture) document.exitPictureInPicture().catch(() => {});
      },
    };
    Object.entries(handlers).forEach(([evt, fn]) => el.addEventListener(evt, fn));
    return () => Object.entries(handlers).forEach(([evt, fn]) => el.removeEventListener(evt, fn));
  }, [videoRef, videoId]);

  // ---------- Защита от скачивания ----------
  useEffect(() => {
    const preventDownload = (e) => {
      if (e.type === 'contextmenu') e.preventDefault();
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')) ||
        (e.ctrlKey && (e.key === 'u' || e.key === 'U')) ||
        (e.ctrlKey && (e.key === 's' || e.key === 'S'))
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', preventDownload);
    document.addEventListener('keydown', preventDownload);
    return () => {
      document.removeEventListener('contextmenu', preventDownload);
      document.removeEventListener('keydown', preventDownload);
    };
  }, []);

  // ---------- Полноэкранный режим ----------
  useEffect(() => {
    const onChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setFullscreen(!!fsEl && fsEl === containerRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  useEffect(() => {
    if (!pseudoFullscreen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [pseudoFullscreen]);

  const toggleFullscreen = useCallback(() => {
    const cont = containerRef.current;
    if (!cont) return;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      return;
    }
    if (pseudoFullscreen) {
      setPseudoFullscreen(false);
      return;
    }
    const request = cont.requestFullscreen || cont.webkitRequestFullscreen;
    if (request) {
      Promise.resolve(request.call(cont))
        .then(() => {
          // На телефонах разворачиваем в альбомную ориентацию
          if (window.screen && window.screen.orientation && window.screen.orientation.lock) {
            window.screen.orientation.lock('landscape').catch(() => {});
          }
        })
        .catch(() => setPseudoFullscreen(true));
    } else {
      // iPhone: нативный fullscreen для элементов недоступен — растягиваем плеер на весь экран,
      // чтобы водяной знак оставался поверх видео
      setPseudoFullscreen(true);
    }
  }, [pseudoFullscreen]);

  // ---------- Показ / скрытие панели ----------
  const keepControls = !playing || menu !== null || scrubRatio !== null || helpOpen || ended;

  const pokeControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), HIDE_DELAY);
  }, []);

  useEffect(() => () => {
    clearTimeout(hideTimerRef.current);
    clearTimeout(osdTimerRef.current);
    clearTimeout(tapRef.current.timer);
  }, []);

  const showUi = controlsVisible || keepControls;

  // ---------- Действия ----------
  const flash = useCallback((icon, text) => {
    setOsd({ icon, text, key: Date.now() });
    clearTimeout(osdTimerRef.current);
    osdTimerRef.current = setTimeout(() => setOsd(null), 700);
  }, []);

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused || el.ended) el.play().catch(() => {});
    else el.pause();
  }, [videoRef]);

  const seekBy = useCallback((delta, withRipple = true) => {
    const el = videoRef.current;
    if (!el || !el.duration) return;
    el.currentTime = Math.max(0, Math.min(el.duration - 0.1, el.currentTime + delta));
    if (withRipple) setRipple({ side: delta < 0 ? 'left' : 'right', text: `${delta > 0 ? '+' : '−'}${Math.abs(delta)} сек`, key: Date.now() });
  }, [videoRef]);

  const seekTo = useCallback((sec) => {
    const el = videoRef.current;
    if (!el || !el.duration) return;
    el.currentTime = Math.max(0, Math.min(el.duration - 0.1, sec));
  }, [videoRef]);

  const changeVolume = useCallback((v, announce) => {
    const nv = Math.max(0, Math.min(1, Math.round(v * 100) / 100));
    setVolume(nv);
    setMuted(nv === 0);
    if (announce) flash(nv === 0 ? 'mute' : 'volume', `${Math.round(nv * 100)}%`);
  }, [flash]);

  const toggleMute = useCallback(() => {
    const nm = !muted;
    setMuted(nm);
    if (!nm && volume === 0) setVolume(0.6);
    flash(nm ? 'mute' : 'volume', nm ? 'Без звука' : `${Math.round((volume || 0.6) * 100)}%`);
  }, [muted, volume, flash]);

  const changeRate = useCallback((dir) => {
    const current = videoRef.current ? videoRef.current.playbackRate : rate;
    const idx = SPEEDS.indexOf(current);
    const nextIdx = Math.max(0, Math.min(SPEEDS.length - 1, (idx === -1 ? 2 : idx) + dir));
    if (videoRef.current) videoRef.current.playbackRate = SPEEDS[nextIdx];
    setRate(SPEEDS[nextIdx]);
    flash('speed', `${SPEEDS[nextIdx]}×`);
  }, [rate, flash, videoRef]);

  const selectLevel = (idx) => {
    setLevel(idx);
    if (hlsRef.current) hlsRef.current.currentLevel = idx;
    setMenu(null);
  };

  const goNext = useCallback(() => {
    if (next && onNext) onNext();
  }, [next, onNext]);

  // ---------- Автопереход к следующему видео ----------
  useEffect(() => {
    if (!ended || !next || !autoNext) return;
    setCountdown(AUTONEXT_SECONDS);
  }, [ended, next, autoNext]);

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown <= 0) {
      goNext();
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown, goNext]);

  // ---------- Плашка «продолжено с …» ----------
  useEffect(() => {
    if (!resumeChip || !started) return undefined;
    const t = setTimeout(() => setResumeChip(null), 7000);
    return () => clearTimeout(t);
  }, [resumeChip, started]);

  // ---------- Media Session (кнопки на гарнитуре / экране блокировки) ----------
  useEffect(() => {
    const ms = navigator.mediaSession;
    if (!ms || !window.MediaMetadata) return undefined;
    try {
      ms.metadata = new window.MediaMetadata({ title: title || '' });
      ms.setActionHandler('play', () => videoRef.current && videoRef.current.play().catch(() => {}));
      ms.setActionHandler('pause', () => videoRef.current && videoRef.current.pause());
      ms.setActionHandler('seekbackward', () => seekBy(-10, false));
      ms.setActionHandler('seekforward', () => seekBy(10, false));
      ms.setActionHandler('nexttrack', next ? goNext : null);
    } catch {
      /* не все действия поддерживаются */
    }
    return () => {
      try {
        ['play', 'pause', 'seekbackward', 'seekforward', 'nexttrack'].forEach((a) => ms.setActionHandler(a, null));
      } catch {
        /* noop */
      }
    };
  }, [title, next, goNext, seekBy, videoRef]);

  // ---------- Горячие клавиши ----------
  const keyHandlerRef = useRef(null);
  keyHandlerRef.current = (e) => {
    if (isTypingTarget(e.target) || e.ctrlKey || e.metaKey || e.altKey) return;
    const el = videoRef.current;
    if (!el) return;
    const k = e.key;
    let handled = true;
    switch (k) {
      case ' ':
      case 'k':
      case 'K':
      case 'л':
      case 'Л':
        if (k === ' ' && e.target && e.target.tagName === 'BUTTON') {
          handled = false;
          break;
        }
        togglePlay();
        flash(el.paused ? 'pause' : 'play');
        break;
      case 'ArrowLeft':
        seekBy(-5);
        break;
      case 'ArrowRight':
        seekBy(5);
        break;
      case 'j':
      case 'J':
      case 'о':
      case 'О':
        seekBy(-10);
        break;
      case 'l':
      case 'L':
      case 'д':
      case 'Д':
        seekBy(10);
        break;
      case 'ArrowUp':
        changeVolume((muted ? 0 : volume) + 0.05, true);
        break;
      case 'ArrowDown':
        changeVolume((muted ? 0 : volume) - 0.05, true);
        break;
      case 'm':
      case 'M':
      case 'ь':
      case 'Ь':
        toggleMute();
        break;
      case 'f':
      case 'F':
      case 'а':
      case 'А':
        toggleFullscreen();
        break;
      case 't':
      case 'T':
      case 'е':
      case 'Е':
        if (onToggleTheater && !isFs) onToggleTheater();
        else handled = false;
        break;
      case '>':
      case 'Ю':
        changeRate(1);
        break;
      case '<':
      case 'Б':
        changeRate(-1);
        break;
      case '.':
      case ',':
        if (e.shiftKey) changeRate(k === '.' ? 1 : -1);
        else handled = false;
        break;
      case 'N':
      case 'Т':
        if (e.shiftKey && next) goNext();
        else handled = false;
        break;
      case 'Home':
        seekTo(0);
        break;
      case 'End':
        seekTo(el.duration);
        break;
      case '/':
        if (!e.shiftKey) {
          handled = false;
          break;
        }
        setHelpOpen((o) => !o);
        break;
      case '?':
        setHelpOpen((o) => !o);
        break;
      case 'Escape':
        if (menu) setMenu(null);
        else if (helpOpen) setHelpOpen(false);
        else if (pseudoFullscreen) setPseudoFullscreen(false);
        else handled = false;
        break;
      default:
        if (/^[0-9]$/.test(k) && el.duration) {
          seekTo((el.duration * parseInt(k, 10)) / 10);
        } else {
          handled = false;
        }
    }
    if (handled) {
      e.preventDefault();
      pokeControls();
    }
  };

  useEffect(() => {
    const onKey = (e) => keyHandlerRef.current && keyHandlerRef.current(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Закрытие меню по клику снаружи
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && !e.target.closest('.vp-settings-btn')) setMenu(null);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [menu]);

  // ---------- Прогресс-бар (перемотка с перетаскиванием) ----------
  const ratioFromEvent = (e) => {
    const bar = progressRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  };

  const onProgressPointerDown = (e) => {
    if (!duration) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
    setScrubRatio(ratioFromEvent(e));
  };

  const onProgressPointerMove = (e) => {
    const r = ratioFromEvent(e);
    setHoverRatio(r);
    if (scrubRatio !== null) setScrubRatio(r);
  };

  const onProgressPointerUp = (e) => {
    if (scrubRatio === null) return;
    seekTo(ratioFromEvent(e) * duration);
    setScrubRatio(null);
  };

  const onProgressKeyDown = (e) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      seekBy(e.key === 'ArrowLeft' ? -5 : 5);
    }
  };

  // ---------- Клики и тапы по видео ----------
  const onSurfacePointerDown = (e) => {
    pointerTypeRef.current = e.pointerType || 'mouse';
  };

  const onSurfaceClick = (e) => {
    if (menu) {
      setMenu(null);
      return;
    }
    if (pointerTypeRef.current !== 'touch') {
      const el = videoRef.current;
      togglePlay();
      if (el) flash(el.paused ? 'pause' : 'play');
      return;
    }
    // Тач: одиночный тап — показать/скрыть панель, двойной по краям — перемотка
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const now = Date.now();
    const tap = tapRef.current;
    if (now - tap.time < 300) {
      clearTimeout(tap.timer);
      tap.time = 0;
      if (x < 0.35) seekBy(-10);
      else if (x > 0.65) seekBy(10);
      else togglePlay();
      return;
    }
    tap.time = now;
    tap.timer = setTimeout(() => {
      if (showUi && playing) {
        clearTimeout(hideTimerRef.current);
        setControlsVisible(false);
      } else {
        pokeControls();
      }
    }, 260);
  };

  const onSurfaceDoubleClick = () => {
    if (pointerTypeRef.current !== 'touch') toggleFullscreen();
  };

  // ---------- Отображение ----------
  const shownTime = scrubRatio !== null ? scrubRatio * duration : time;
  const playedPct = duration ? (shownTime / duration) * 100 : 0;
  const hoverTime = hoverRatio !== null ? hoverRatio * duration : null;
  const sortedLevels = [...levels].sort((a, b) => b.height - a.height);
  const qualityLabel = level === -1 ? `Авто${autoHeight ? ` · ${autoHeight}p` : ''}` : `${(levels[level] || {}).height || ''}p`;
  const currentHeight = level === -1 ? autoHeight : (levels[level] || {}).height;
  const VolumeIcon = muted || volume === 0 ? IconMute : volume < 0.5 ? IconVolumeLow : IconVolume;
  const volPct = Math.round((muted ? 0 : volume) * 100);

  const osdIcon = (name) => {
    switch (name) {
      case 'play': return <IconPlay size={30} />;
      case 'pause': return <IconPause size={30} />;
      case 'mute': return <IconMute size={28} />;
      case 'volume': return <IconVolume size={28} />;
      case 'speed': return <IconSpeed size={28} />;
      default: return null;
    }
  };

  return (
    <div
      ref={containerRef}
      className={[
        'vp',
        isFs ? 'vp-fullscreen' : '',
        pseudoFullscreen ? 'vp-pseudo-fs' : '',
        showUi ? 'vp-ui-visible' : 'vp-ui-hidden',
        started ? 'vp-started' : '',
        theater ? 'vp-theater' : '',
      ].join(' ')}
      onPointerMove={(e) => {
        if (e.pointerType === 'mouse') pokeControls();
      }}
      onMouseLeave={() => {
        clearTimeout(hideTimerRef.current);
        if (playing) hideTimerRef.current = setTimeout(() => setControlsVisible(false), 400);
      }}
      onFocusCapture={pokeControls}
    >
      <video
        ref={videoRef}
        className="vp-video"
        controls={false}
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        disableRemotePlayback
        playsInline
        preload="auto"
        onContextMenu={(e) => e.preventDefault()}
      >
        Ваш браузер не поддерживает воспроизведение видео.
      </video>

      {overlay}

      {/* Поверхность для кликов / тапов */}
      <div
        className="vp-surface"
        onPointerDown={onSurfacePointerDown}
        onClick={onSurfaceClick}
        onDoubleClick={onSurfaceDoubleClick}
        aria-hidden="true"
      />

      {/* Верхний градиент с названием в полноэкранном режиме */}
      {(isFs || showTitle) && (
        <div className="vp-top">
          <div className="vp-top-title">{title}</div>
        </div>
      )}

      {/* Буферизация */}
      {waiting && !ended && (
        <div className="vp-loader" aria-label="Загрузка">
          <span />
        </div>
      )}

      {/* Большая кнопка до старта / на паузе */}
      {!playing && !ended && !waiting && (
        <button
          type="button"
          className={`vp-bigplay ${started ? 'vp-bigplay-paused' : ''}`}
          onClick={togglePlay}
          aria-label="Воспроизвести"
        >
          <IconPlay size={34} />
        </button>
      )}

      {/* Волна при перемотке */}
      {ripple && (
        <div key={ripple.key} className={`vp-ripple vp-ripple-${ripple.side}`} onAnimationEnd={() => setRipple(null)}>
          <div className="vp-ripple-inner">
            {ripple.side === 'left' ? <IconBack10 size={26} /> : <IconFwd10 size={26} />}
            <span>{ripple.text}</span>
          </div>
        </div>
      )}

      {/* Индикатор действия (громкость, скорость, пауза) */}
      {osd && (
        <div key={osd.key} className="vp-osd">
          {osdIcon(osd.icon)}
          {osd.text && <span>{osd.text}</span>}
        </div>
      )}

      {/* «Продолжено с …» */}
      {resumeChip && started && !ended && (
        <div className="vp-resume">
          <span>Продолжаем с {formatTime(resumeChip)}</span>
          <button
            type="button"
            onClick={() => {
              seekTo(0);
              setResumeChip(null);
            }}
          >
            С начала
          </button>
        </div>
      )}

      {/* Экран окончания */}
      {ended && (
        <div className="vp-endscreen">
          {next ? (
            <div className="vp-end-card">
              <div className="vp-end-label">Следующее видео</div>
              <div className="vp-end-title">{next.title || 'Продолжение плейлиста'}</div>
              <div className="vp-end-actions">
                <button type="button" className="vp-end-primary" onClick={goNext}>
                  {countdown !== null && (
                    <svg className="vp-countdown" viewBox="0 0 36 36" aria-hidden="true">
                      <circle cx="18" cy="18" r="15" />
                      <circle
                        cx="18"
                        cy="18"
                        r="15"
                        className="vp-countdown-fill"
                        style={{ strokeDashoffset: 94.25 * (1 - countdown / AUTONEXT_SECONDS) }}
                      />
                    </svg>
                  )}
                  <IconNext size={18} />
                  {countdown !== null ? `Через ${countdown} сек` : 'Смотреть'}
                </button>
                {countdown !== null ? (
                  <button type="button" className="vp-end-secondary" onClick={() => setCountdown(null)}>
                    Отмена
                  </button>
                ) : (
                  <button
                    type="button"
                    className="vp-end-secondary"
                    onClick={() => {
                      seekTo(0);
                      togglePlay();
                    }}
                  >
                    <IconReplay size={16} /> Повторить
                  </button>
                )}
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="vp-replay"
              onClick={() => {
                seekTo(0);
                togglePlay();
              }}
            >
              <IconReplay size={30} />
              <span>Смотреть снова</span>
            </button>
          )}
        </div>
      )}

      {/* Горячие клавиши */}
      {helpOpen && (
        <div className="vp-help" role="dialog" aria-label="Горячие клавиши" onClick={() => setHelpOpen(false)}>
          <div className="vp-help-card" onClick={(e) => e.stopPropagation()}>
            <div className="vp-help-head">
              <IconKeyboard size={18} />
              <span>Горячие клавиши</span>
              <button type="button" onClick={() => setHelpOpen(false)} aria-label="Закрыть">×</button>
            </div>
            <dl>
              {SHORTCUTS.map(([key, label]) => (
                <div key={key} className="vp-help-row">
                  <dt>{key.split(' / ').map((part, i) => (
                    <React.Fragment key={part}>
                      {i > 0 && <span className="vp-help-sep">/</span>}
                      <kbd>{part}</kbd>
                    </React.Fragment>
                  ))}</dt>
                  <dd>{label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {/* Тонкая полоска прогресса, когда панель скрыта */}
      <div className="vp-mini-progress" aria-hidden="true">
        <div style={{ width: `${playedPct}%` }} />
      </div>

      {/* Панель управления */}
      <div className="vp-controls" onClick={(e) => e.stopPropagation()} onPointerDown={pokeControls}>
        <div
          ref={progressRef}
          className={`vp-progress ${scrubRatio !== null ? 'vp-scrubbing' : ''}`}
          role="slider"
          tabIndex={0}
          aria-label="Перемотка"
          aria-valuemin={0}
          aria-valuemax={Math.floor(duration)}
          aria-valuenow={Math.floor(shownTime)}
          aria-valuetext={`${formatTime(shownTime)} из ${formatTime(duration)}`}
          onPointerDown={onProgressPointerDown}
          onPointerMove={onProgressPointerMove}
          onPointerUp={onProgressPointerUp}
          onPointerCancel={() => setScrubRatio(null)}
          onPointerLeave={() => setHoverRatio(null)}
          onKeyDown={onProgressKeyDown}
        >
          <div className="vp-progress-track">
            <div className="vp-progress-buffer" style={{ width: `${buffered}%` }} />
            {hoverRatio !== null && <div className="vp-progress-hover" style={{ width: `${hoverRatio * 100}%` }} />}
            <div className="vp-progress-played" style={{ width: `${playedPct}%` }} />
          </div>
          <div className="vp-progress-thumb" style={{ left: `${playedPct}%` }} />
          {(hoverTime !== null || scrubRatio !== null) && duration > 0 && (
            <div
              className="vp-progress-tooltip"
              style={{ left: `clamp(28px, ${(scrubRatio !== null ? scrubRatio : hoverRatio) * 100}%, calc(100% - 28px))` }}
            >
              {formatTime(scrubRatio !== null ? shownTime : hoverTime)}
            </div>
          )}
        </div>

        <div className="vp-row">
          <div className="vp-group">
            <button type="button" className="vp-btn vp-btn-play" onClick={togglePlay} aria-label={playing ? 'Пауза (K)' : 'Воспроизвести (K)'} data-tip={playing ? 'Пауза' : 'Смотреть'}>
              {ended ? <IconReplay size={22} /> : playing ? <IconPause size={22} /> : <IconPlay size={22} />}
            </button>
            <button type="button" className="vp-btn vp-hide-xs" onClick={() => seekBy(-10)} aria-label="Назад на 10 секунд (J)" data-tip="−10 сек">
              <IconBack10 size={22} />
            </button>
            <button type="button" className="vp-btn vp-hide-xs" onClick={() => seekBy(10)} aria-label="Вперёд на 10 секунд (L)" data-tip="+10 сек">
              <IconFwd10 size={22} />
            </button>
            {next && (
              <button type="button" className="vp-btn" onClick={goNext} aria-label="Следующее видео (Shift+N)" data-tip="Следующее">
                <IconNext size={20} />
              </button>
            )}
            <div className="vp-volume">
              <button type="button" className="vp-btn" onClick={toggleMute} aria-label={muted ? 'Включить звук (M)' : 'Выключить звук (M)'}>
                <VolumeIcon size={22} />
              </button>
              <input
                type="range"
                className="vp-volume-slider"
                min="0"
                max="1"
                step="0.01"
                value={muted ? 0 : volume}
                onChange={(e) => changeVolume(parseFloat(e.target.value), false)}
                style={{ '--val': `${volPct}%` }}
                aria-label="Громкость"
                aria-valuetext={`${volPct}%`}
              />
            </div>
            <button
              type="button"
              className="vp-time"
              onClick={() => setShowRemaining((r) => !r)}
              aria-label="Переключить отображение времени"
            >
              <span className="vp-time-current">{showRemaining ? `−${formatTime(duration - shownTime)}` : formatTime(shownTime)}</span>
              <span className="vp-time-sep">/</span>
              <span className="vp-time-total">{formatTime(duration)}</span>
            </button>
          </div>

          <div className="vp-group">
            {rate !== 1 && (
              <button type="button" className="vp-chip vp-chip-btn" onClick={() => setMenu('speed')} aria-label="Скорость воспроизведения">
                {rate}×
              </button>
            )}
            {currentHeight >= 720 && <span className="vp-chip vp-chip-hd">HD</span>}
            <div className="vp-settings-wrap">
              <button
                type="button"
                className={`vp-btn vp-settings-btn ${menu ? 'vp-btn-active' : ''}`}
                onClick={() => setMenu((m) => (m ? null : 'main'))}
                aria-label="Настройки"
                aria-expanded={!!menu}
                data-tip="Настройки"
              >
                <IconSettings size={21} />
              </button>
              {menu && (
                <div ref={menuRef} className="vp-menu" role="menu">
                  {menu === 'main' && (
                    <>
                      <button type="button" className="vp-menu-item" onClick={() => setMenu('speed')}>
                        <IconSpeed size={18} />
                        <span className="vp-menu-label">Скорость</span>
                        <span className="vp-menu-value">{rate === 1 ? 'Обычная' : `${rate}×`}</span>
                        <IconChevronRight size={16} />
                      </button>
                      {levels.length > 1 && (
                        <button type="button" className="vp-menu-item" onClick={() => setMenu('quality')}>
                          <IconHd size={18} />
                          <span className="vp-menu-label">Качество</span>
                          <span className="vp-menu-value">{qualityLabel}</span>
                          <IconChevronRight size={16} />
                        </button>
                      )}
                      {next && (
                        <button type="button" className="vp-menu-item" role="menuitemcheckbox" aria-checked={autoNext} onClick={() => setAutoNext((a) => !a)}>
                          <IconNext size={18} />
                          <span className="vp-menu-label">Автопереход</span>
                          <span className={`vp-switch ${autoNext ? 'on' : ''}`} />
                        </button>
                      )}
                      <button type="button" className="vp-menu-item" onClick={() => { setMenu(null); setHelpOpen(true); }}>
                        <IconKeyboard size={18} />
                        <span className="vp-menu-label">Горячие клавиши</span>
                        <span className="vp-menu-value">?</span>
                      </button>
                    </>
                  )}
                  {menu === 'speed' && (
                    <>
                      <button type="button" className="vp-menu-back" onClick={() => setMenu('main')}>
                        <IconChevronLeft size={16} /> Скорость
                      </button>
                      <div className="vp-speed-grid">
                        {SPEEDS.map((s) => (
                          <button
                            key={s}
                            type="button"
                            className={`vp-speed ${rate === s ? 'active' : ''}`}
                            onClick={() => {
                              setRate(s);
                              setMenu(null);
                            }}
                          >
                            {s === 1 ? '1×' : `${s}×`}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {menu === 'quality' && (
                    <>
                      <button type="button" className="vp-menu-back" onClick={() => setMenu('main')}>
                        <IconChevronLeft size={16} /> Качество
                      </button>
                      {sortedLevels.map((l) => (
                        <button key={l.index} type="button" className="vp-menu-item vp-menu-option" onClick={() => selectLevel(l.index)}>
                          <span className="vp-menu-check">{level === l.index && <IconCheck size={16} />}</span>
                          <span className="vp-menu-label">{l.height}p</span>
                          {l.height >= 720 && <span className="vp-chip vp-chip-hd">HD</span>}
                        </button>
                      ))}
                      <button type="button" className="vp-menu-item vp-menu-option" onClick={() => selectLevel(-1)}>
                        <span className="vp-menu-check">{level === -1 && <IconCheck size={16} />}</span>
                        <span className="vp-menu-label">Авто</span>
                        {autoHeight && <span className="vp-menu-value">{autoHeight}p</span>}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {onToggleTheater && !isFs && (
              <button type="button" className={`vp-btn vp-hide-sm ${theater ? 'vp-btn-active' : ''}`} onClick={onToggleTheater} aria-label="Широкий режим (T)" data-tip="Широкий режим">
                <IconTheater size={22} />
              </button>
            )}
            <button type="button" className="vp-btn" onClick={toggleFullscreen} aria-label={isFs ? 'Выйти из полноэкранного режима (F)' : 'Полный экран (F)'} data-tip={isFs ? 'Свернуть' : 'Полный экран'}>
              {isFs ? <IconFullscreenExit size={21} /> : <IconFullscreen size={21} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Player;
